/**
 * Candidate Match Card - Displays AI-ranked candidate with match score
 */
import React from 'react';
import { MapPin, Briefcase, Award, ThumbsUp, ThumbsDown, UserPlus, Eye, ExternalLink } from 'lucide-react';

const CandidateMatchCard = ({
  match,
  onAddToPipeline,
  onViewProfile,
  onFeedback,
  showActions = true
}) => {
  const getMatchColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-50 border-green-200';
    if (score >= 60) return 'text-blue-600 bg-blue-50 border-blue-200';
    if (score >= 40) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-gray-600 bg-gray-50 border-gray-200';
  };

  const getTierBadge = (tier) => {
    const colors = {
      strong: 'bg-green-100 text-green-800',
      medium: 'bg-blue-100 text-blue-800',
      low: 'bg-yellow-100 text-yellow-800',
      weak: 'bg-gray-100 text-gray-800'
    };
    return colors[tier] || colors.weak;
  };

  const getSourceBadge = (source) => {
    // Map actual portal names to styled badges
    const badges = {
      'Internal Database': { color: 'bg-purple-100 text-purple-800', label: 'Internal Database' },
      'LinkedIn': { color: 'bg-blue-100 text-blue-800', label: 'LinkedIn' },
      'Indeed': { color: 'bg-green-100 text-green-800', label: 'Indeed' },
      'Monster': { color: 'bg-orange-100 text-orange-800', label: 'Monster' },
      'Dice': { color: 'bg-indigo-100 text-indigo-800', label: 'Dice' },
      'ZipRecruiter': { color: 'bg-pink-100 text-pink-800', label: 'ZipRecruiter' },
      'Glassdoor': { color: 'bg-cyan-100 text-cyan-800', label: 'Glassdoor' }
    };
    return badges[source] || { color: 'bg-gray-100 text-gray-800', label: source };
  };

  return (
    <div className="bg-white border rounded-lg p-4 hover:shadow-md transition">
      {/* Header */}
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            {match.display_id && (
              <span className="inline-block px-2 py-1 text-xs font-mono font-semibold bg-purple-100 text-purple-800 rounded">
                {match.display_id}
              </span>
            )}
            <h3 className="font-semibold text-lg">{match.candidate_name}</h3>
          </div>
          {match.current_title && (
            <p className="text-sm text-gray-600 flex items-center gap-1">
              <Briefcase className="w-4 h-4" />
              {match.current_title}
              {match.current_company && ` at ${match.current_company}`}
            </p>
          )}
        </div>

        {/* Match Score */}
        <div className={`text-center px-4 py-2 rounded-lg border-2 ${getMatchColor(match.match_score.overall_score)}`}>
          <div className="text-2xl font-bold">{match.match_score.overall_score}</div>
          <div className="text-xs uppercase font-medium">Match</div>
        </div>
      </div>

      {/* Details */}
      <div className="flex flex-wrap gap-4 mb-3 text-sm text-gray-600">
        {match.experience_years && (
          <span className="flex items-center gap-1">
            <Award className="w-4 h-4" />
            {match.experience_years} years
          </span>
        )}
        {match.location && (
          <span className="flex items-center gap-1">
            <MapPin className="w-4 h-4" />
            {match.location}
          </span>
        )}
      </div>

      {/* Skills */}
      {match.skills && match.skills.length > 0 && (
        <div className="mb-3">
          <div className="flex flex-wrap gap-1">
            {match.skills.slice(0, 6).map((skill, idx) => (
              <span
                key={idx}
                className={`px-2 py-1 text-xs rounded ${
                  match.matched_skills.includes(skill)
                    ? 'bg-green-100 text-green-700 font-medium'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {skill}
              </span>
            ))}
            {match.skills.length > 6 && (
              <span className="px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded">
                +{match.skills.length - 6} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Match Reason */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
        <p className="text-sm text-blue-900">
          <span className="font-medium">Why they match:</span> {match.match_reason}
        </p>
      </div>

      {/* Badges */}
      <div className="flex flex-wrap gap-2 mb-3">
        <span className={`px-2 py-1 text-xs rounded-full font-medium ${getTierBadge(match.match_score.tier)}`}>
          {match.match_score.tier.toUpperCase()} Match
        </span>
        {match.sources.map((source, idx) => {
          const badge = getSourceBadge(source);
          return (
            <span key={idx} className={`px-2 py-1 text-xs rounded-full ${badge.color}`}>
              {badge.label}
            </span>
          );
        })}
      </div>

      {/* Actions */}
      {showActions && (
        <div className="flex gap-2 pt-3 border-t">
          {match.already_in_pipeline ? (
            <button
              onClick={() => onViewProfile && onViewProfile(match)}
              className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center justify-center gap-2 font-medium"
            >
              <Eye className="w-4 h-4" />
              View Submission
            </button>
          ) : (
            <button
              onClick={() => onAddToPipeline && onAddToPipeline(match)}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 flex items-center justify-center gap-2 font-medium"
            >
              <UserPlus className="w-4 h-4" />
              Submit to Job
            </button>
          )}
          <button
            onClick={() => onViewProfile && onViewProfile(match)}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <ExternalLink className="w-4 h-4" />
          </button>
          <button
            onClick={() => onFeedback && onFeedback(match, 'not_relevant')}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2"
            title="Not relevant"
          >
            <ThumbsDown className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default CandidateMatchCard;
